/**
 * Helpers to merge MLB prediction rows by player for the Team Picks page.
 */

/**
 * @typedef {object} TeamPicksRow
 * @property {number} playerId
 * @property {string} playerName
 * @property {number | null} teamId
 * @property {string} teamName
 * @property {number} battingOrder
 * @property {string | null} [gameStatus]
 * @property {string | null} [gameTime]
 * @property {string | null} [gameId]
 * @property {object | null} [totalBases]
 * @property {object | null} [hit]
 * @property {object | null} [twoHit]
 * @property {object | null} [hr]
 */

/**
 * @param {object} p - Raw prediction row
 * @returns {number | null}
 */
export function normalizeTeamId(p) {
  if (p == null) return null;
  const t = p.teamId ?? p.team_id;
  if (t == null || t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {object[]} predictions
 * @param {string | number} teamId
 * @returns {object[]}
 */
export function filterPredictionsByTeam(predictions, teamId) {
  const tid = Number(teamId);
  if (!Number.isFinite(tid)) return [];
  return (predictions || []).filter((p) => normalizeTeamId(p) === tid);
}

/**
 * @param {object} p - Total bases prediction row
 * @returns {string}
 */
export function formatTotalBasesPredDisplay(p) {
  if (p == null) return '—';
  const v = p.predictedTB2;
  if (v == null) return '—';
  const n = Number(v);
  if (Number.isFinite(n) && n <= 1 && n >= 0) {
    return `${(n * 100).toFixed(1)}%`;
  }
  return Number.isFinite(n) ? n.toFixed(2) : '—';
}

/**
 * @param {object | null | undefined} p - Hit model row
 * @returns {number | null}
 */
export function hitPredValue(p) {
  if (p == null) return null;
  const v = p.predicted_hit_prob ?? p.predictedHitProb;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {object | null | undefined} p
 * @returns {number | null}
 */
export function twoHitPredValue(p) {
  if (p == null) return null;
  const v = p.predicted_two_hit_prob ?? p.predictedTwoHitProb;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {object | null | undefined} p
 * @returns {number | null}
 */
export function hrPredValue(p) {
  if (p == null) return null;
  const v = p.predicted_hr_prob ?? p.predictedHRProb;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {object | null | undefined} p
 * @returns {number | null} American odds for sorting
 */
export function oddsDisplayToSortNumberFromPick(p, oddsKey) {
  if (p == null) return null;
  const d = p[oddsKey]?.display;
  if (d == null || String(d).trim() === '') return null;
  const parsed = parseFloat(String(d).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * @param {object | null | undefined} p
 * @param {string} edgeKey - e.g. total_bases_edge_display
 * @returns {number | null} Edge as fraction (e.g. 0.05) or null
 */
export function edgeValueFromPick(p, edgeKey) {
  if (p == null) return null;
  const raw = p[edgeKey]?.value;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Merge four model lists into one row per player_id.
 *
 * @param {{
 *   totalBases: object[],
 *   hit: object[],
 *   twoHit: object[],
 *   hr: object[],
 * }} lists
 * @returns {TeamPicksRow[]}
 */
export function mergePlayerRows(lists) {
  const { totalBases, hit, twoHit, hr } = lists;
  /** @type {Map<number, TeamPicksRow>} */
  const map = new Map();

  /**
   * @param {object} p
   * @param {'totalBases' | 'hit' | 'twoHit' | 'hr'} key
   */
  const add = (p, key) => {
    const rawId = p.playerId ?? p.player_id;
    if (rawId == null) return;
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    let row = map.get(id);
    const bo = Number(p.battingOrder ?? p.batting_order ?? -1);
    if (!row) {
      row = {
        playerId: id,
        playerName: p.playerName ?? p.player_name ?? '—',
        teamId: normalizeTeamId(p),
        teamName: p.teamName ?? p.team_name ?? '',
        battingOrder: bo,
        gameStatus: p.gameStatus ?? p.game_status ?? null,
        gameTime: p.gameTime ?? p.game_time ?? null,
        gameId: p.gameId ?? p.game_id ?? null,
        totalBases: null,
        hit: null,
        twoHit: null,
        hr: null,
      };
      map.set(id, row);
    }
    row[key] = p;
    if (bo > 0 && (row.battingOrder <= 0 || row.battingOrder === -1)) {
      row.battingOrder = bo;
    }
    const name = p.playerName ?? p.player_name;
    if (name && String(name).trim()) {
      row.playerName = name;
    }
  };

  (totalBases || []).forEach((p) => add(p, 'totalBases'));
  (hit || []).forEach((p) => add(p, 'hit'));
  (twoHit || []).forEach((p) => add(p, 'twoHit'));
  (hr || []).forEach((p) => add(p, 'hr'));

  return Array.from(map.values());
}

/**
 * @param {TeamPicksRow} row
 * @param {'all' | 'in_lineup' | 'bench' | 'not_posted'} lineupFilter
 * @returns {boolean}
 */
export function passesLineupFilter(row, lineupFilter) {
  const bo = Number(row.battingOrder ?? -1);
  if (lineupFilter === 'all') return true;
  if (lineupFilter === 'in_lineup') return bo > 0;
  if (lineupFilter === 'bench') return bo === 0;
  if (lineupFilter === 'not_posted') return bo === -1;
  return true;
}

/**
 * @param {TeamPicksRow} row
 * @param {number | null} minAmerican - Minimum American odds (numeric: +150, -120)
 * @param {'any' | 'total_bases' | 'hit' | 'two_hit' | 'hr'} oddsScope
 * @returns {boolean}
 */
export function passesMinOddsFilter(row, minAmerican, oddsScope) {
  if (minAmerican == null || !Number.isFinite(minAmerican)) return true;
  if (oddsScope === 'total_bases') {
    const o = oddsDisplayToSortNumberFromPick(row.totalBases, 'total_bases_odds_display');
    return o != null && o >= minAmerican;
  }
  if (oddsScope === 'hit') {
    const o = oddsDisplayToSortNumberFromPick(row.hit, 'hit_odds_display');
    return o != null && o >= minAmerican;
  }
  if (oddsScope === 'two_hit') {
    const o = oddsDisplayToSortNumberFromPick(row.twoHit, 'two_hit_odds_display');
    return o != null && o >= minAmerican;
  }
  if (oddsScope === 'hr') {
    const o = oddsDisplayToSortNumberFromPick(row.hr, 'hr_odds_display');
    return o != null && o >= minAmerican;
  }
  const vals = [
    oddsDisplayToSortNumberFromPick(row.totalBases, 'total_bases_odds_display'),
    oddsDisplayToSortNumberFromPick(row.hit, 'hit_odds_display'),
    oddsDisplayToSortNumberFromPick(row.twoHit, 'two_hit_odds_display'),
    oddsDisplayToSortNumberFromPick(row.hr, 'hr_odds_display'),
  ].filter((o) => o != null && Number.isFinite(o));
  if (vals.length === 0) return false;
  return vals.some((o) => o >= minAmerican);
}

/**
 * @param {TeamPicksRow} row
 * @param {number | null} minEdgeFrac - Minimum edge as fraction (e.g. 0.02 for 2%)
 * @param {'any' | 'total_bases' | 'hit' | 'two_hit' | 'hr'} edgeScope
 * @returns {boolean}
 */
export function passesMinEdgeFilter(row, minEdgeFrac, edgeScope) {
  if (minEdgeFrac == null || !Number.isFinite(minEdgeFrac)) return true;
  if (edgeScope === 'total_bases') {
    const e = edgeValueFromPick(row.totalBases, 'total_bases_edge_display');
    return e != null && e >= minEdgeFrac;
  }
  if (edgeScope === 'hit') {
    const e = edgeValueFromPick(row.hit, 'hit_edge_display');
    return e != null && e >= minEdgeFrac;
  }
  if (edgeScope === 'two_hit') {
    const e = edgeValueFromPick(row.twoHit, 'two_hit_edge_display');
    return e != null && e >= minEdgeFrac;
  }
  if (edgeScope === 'hr') {
    const e = edgeValueFromPick(row.hr, 'hr_edge_display');
    return e != null && e >= minEdgeFrac;
  }
  const edges = [
    edgeValueFromPick(row.totalBases, 'total_bases_edge_display'),
    edgeValueFromPick(row.hit, 'hit_edge_display'),
    edgeValueFromPick(row.twoHit, 'two_hit_edge_display'),
    edgeValueFromPick(row.hr, 'hr_edge_display'),
  ].filter((e) => e != null && Number.isFinite(e));
  if (edges.length === 0) return false;
  return edges.some((e) => e >= minEdgeFrac);
}

/**
 * @param {TeamPicksRow} row
 * @param {string} sortColumn
 * @returns {number | string | null}
 */
export function getSortComparable(row, sortColumn) {
  switch (sortColumn) {
    case 'player_name':
      return (row.playerName || '').toLowerCase();
    case 'batting_order': {
      const bo = Number(row.battingOrder ?? -1);
      return Number.isFinite(bo) ? bo : -999;
    }
    case 'tb_pred': {
      const p = row.totalBases;
      if (p == null) return null;
      const v = p.predictedTB2;
      return v != null && v !== '' ? Number(v) : null;
    }
    case 'tb_odds':
      return oddsDisplayToSortNumberFromPick(row.totalBases, 'total_bases_odds_display');
    case 'tb_edge':
      return edgeValueFromPick(row.totalBases, 'total_bases_edge_display');
    case 'hit_pred':
      return hitPredValue(row.hit);
    case 'hit_odds':
      return oddsDisplayToSortNumberFromPick(row.hit, 'hit_odds_display');
    case 'hit_edge':
      return edgeValueFromPick(row.hit, 'hit_edge_display');
    case 'two_pred':
      return twoHitPredValue(row.twoHit);
    case 'two_odds':
      return oddsDisplayToSortNumberFromPick(row.twoHit, 'two_hit_odds_display');
    case 'two_edge':
      return edgeValueFromPick(row.twoHit, 'two_hit_edge_display');
    case 'hr_pred':
      return hrPredValue(row.hr);
    case 'hr_odds':
      return oddsDisplayToSortNumberFromPick(row.hr, 'hr_odds_display');
    case 'hr_edge':
      return edgeValueFromPick(row.hr, 'hr_edge_display');
    default:
      return null;
  }
}

/**
 * @param {TeamPicksRow[]} rows
 * @param {string} sortColumn
 * @param {'asc' | 'desc'} sortDirection
 * @returns {TeamPicksRow[]}
 */
export function sortTeamRows(rows, sortColumn, sortDirection) {
  const mult = sortDirection === 'asc' ? 1 : -1;
  const out = [...rows];
  out.sort((a, b) => {
    const av = getSortComparable(a, sortColumn);
    const bv = getSortComparable(b, sortColumn);
    const aNull = av == null || av === '';
    const bNull = bv == null || bv === '';
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof av === 'string' && typeof bv === 'string') {
      if (av < bv) return -mult;
      if (av > bv) return mult;
      return 0;
    }
    const an = Number(av);
    const bn = Number(bv);
    if (an < bn) return -mult;
    if (an > bn) return mult;
    return 0;
  });
  return out;
}
